import BaseService from "services/base";
import { builder } from "utils/graphql";
import { CAMPAIGN_PACKAGE_SELECTION } from "./constant";
import { get } from "utils/lodash";

class Service extends BaseService {
  name = "CampaignPackage";
  static override DEFINITION = builder.createDefinition({
    name: "CampaignPackage",
    baseQuery: "CampaignPackage",
    baseSelection: `
        ${CAMPAIGN_PACKAGE_SELECTION}
    `,
    GQL_SHAPE_QUERY: {
      BASE: `
        {{ selections }}
      `,
      FIND: `
        docs {
          {{ selections }}
        }
      `,
    },
    GQL_ACTIONS: {
      FIND: "CampaignPackages",
      CREATE: "createCampaignPackage",
      UPDATE: "updateCampaignPackage",
      DELETE: "",
    },
    GQL_RESPONSE_DATA_PATH: {
      BASE: "data.CampaignPackage",
      FIND: "data.CampaignPackages.docs",
      PARAMS: "data.CampaignPackages",
    },
  });

  async create(options: any = {}, selections: string = "") {
    // Call parent create method with full selection
    const result = await super.create(options, selections || CAMPAIGN_PACKAGE_SELECTION);

    // Calculate promotionStatus for the new item if created successfully
    if (result && !result.errors && result.data) {
      result.data.promotionStatus = this.calculatePromotionStatus(result.data);
    }

    return result;
  }

  async update(options: any = {}, selections: string = "") {
    // Call parent update method with full selection
    const result = await super.update(options, selections || CAMPAIGN_PACKAGE_SELECTION);

    // Calculate promotionStatus for the updated item if successful
    if (result && !result.errors && result.data) {
      result.data.promotionStatus = this.calculatePromotionStatus(result.data);
    }

    return result;
  }

  async addFavCampaignPackage(options: any) {
    const nameQuery = "addFavCampaignPackage";
    // const customSelection = selections || SHOP_SELECTION;
    const campaignPackageId = get(options, "campaignPackageId", "");
    return this.custom(
      {
        typeQL: "mutation",
        PATH_RESPONSE: {
          DATA: `data.${nameQuery}`,
          PARAMS: "",
        },

        variables: {
          campaignPackageId,
        },
      },
      `mutation ${nameQuery}($campaignPackageId: String!) {
        ${nameQuery}(campaignPackageId: $campaignPackageId) {
          id
        }
      }`
    );
  }
  async removeFavCampaignPackage(options: any) {
    const nameQuery = "removeFavCampaignPackage";
    const campaignPackageId = get(options, "campaignPackageId", "");
    return this.custom(
      {
        typeQL: "mutation",
        PATH_RESPONSE: {
          DATA: `data.${nameQuery}`,
          PARAMS: "",
        },
        variables: {
          campaignPackageId,
        },
      },
      `mutation ${nameQuery}($campaignPackageId: String!) {
        ${nameQuery}(campaignPackageId: $campaignPackageId) {
          id
        }
      }`
    );
  }
  async getFavCampaignPackages(options: object = {}, selections: string = "") {
    const nameQuery = "myFavCampaignPackages";
    const customSelection = selections || CAMPAIGN_PACKAGE_SELECTION;
    return this.custom(
      {
        typeQL: "query",
        PATH_RESPONSE: {
          DATA: "data.myFavCampaignPackages",
          PARAMS: "",
        },
        ...options,
      },
      `query ${nameQuery} {
        ${nameQuery} {
          ${customSelection}
        }
      }`
    );
  }

  async updatePromotionStatus(campaignPackageId: string, status: string) {
    // First, fetch the current campaign package data
    const getCampaignQuery = "CampaignPackage";
    const currentData = await this.custom(
      {
        typeQL: "query",
        PATH_RESPONSE: {
          DATA: `data.${getCampaignQuery}`,
          PARAMS: "",
          ERRORS: "errors"
        },
        variables: {
          id: campaignPackageId
        }
      },
      `query ${getCampaignQuery}($id: String!) {
        ${getCampaignQuery}(id: $id) {
          id
          name
          type
          dayOfWeek
          usingTimes
          startTime
          endTime
          startDate
          endDate
          discountType
          discountValue
          service {
            id
          }
          shop {
            id
          }
        }
      }`
    );

    if (!currentData || currentData.errors) {
      console.error('Failed to fetch campaign package:', currentData?.errors);
      return { errors: currentData?.errors || [{ message: 'Failed to fetch campaign package' }] };
    }

    const existingData = currentData.data;

    // Now update with all required fields plus the new status
    const nameQuery = "updateCampaignPackage";
    const result = await this.custom(
      {
        typeQL: "mutation",
        PATH_RESPONSE: {
          DATA: `data.${nameQuery}`,
          PARAMS: "",
          ERRORS: "errors"
        },
        variables: {
          id: campaignPackageId,
          data: {
            name: existingData.name,
            type: existingData.type,
            dayOfWeek: existingData.dayOfWeek,
            usingTimes: existingData.usingTimes,
            status: status,
            startTime: existingData.startTime,
            endTime: existingData.endTime,
            startDate: existingData.startDate,
            endDate: existingData.endDate,
            discountType: existingData.discountType,
            discountValue: existingData.discountValue,
            service: existingData.service?.id,
            shop: existingData.shop?.id
          }
        }
      },
      `mutation ${nameQuery}($id: String!, $data: mutationCampaignPackageUpdateInput!) {
        ${nameQuery}(id: $id, data: $data) {
          ${CAMPAIGN_PACKAGE_SELECTION}
        }
      }`
    );

    // Calculate promotionStatus for the updated item
    if (result && !result.errors && result.data) {
      result.data.promotionStatus = this.calculatePromotionStatus({
        ...result.data,
        status: status
      });
    }

    return result;
  }

  calculatePromotionStatus(campaignPackage: any) {
    const now = new Date();
    const startDate = new Date(campaignPackage.startDate);
    const endDate = new Date(campaignPackage.endDate);

    if (campaignPackage.status === "blocked") {
      return "blocked";
    }

    if (campaignPackage.status === "waiting") {
      return "waiting";
    }

    if (campaignPackage.status === "available") {
      if (now < startDate) {
        return "upcoming";
      } else if (now > endDate) {
        return "expired";
      } else {
        return "active";
      }
    }

    return "waiting";
  }
}
// export * from "./formatter";
const Instance = new Service();

export default Instance;
